import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Star, Sparkles, Download, Printer, Receipt } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useSubscription } from "../../hooks/useSubscription";
import { EmployerTabs } from "../../components/employer/EmployerTabs";
import { downloadReceiptTxt, printReceipt } from "../../lib/receipt";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";

export default function EmployerPlans() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { plans, effectivePlan, sub, trialDaysLeft, loading, reload } = useSubscription();
  const [busy, setBusy] = useState(null);
  const [autoRenew, setAutoRenew] = useState(true);
  const [successOpen, setSuccessOpen] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [params, setParams] = useSearchParams();
  const lang = i18n.language?.startsWith("en") ? "en" : "es";

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    api.get(`/billing/verify?session_id=${sessionId}`).then(({ data }) => {
      if (data?.payment_status === "paid") {
        setReceipt({ session_id: sessionId, plan: data.plan, amount: data.amount, currency: data.currency, receipt_number: data.receipt_number, payment_status: "paid", created_at: new Date().toISOString() });
        setSuccessOpen(true);
        reload();
      }
    }).catch(() => {}).finally(() => { params.delete("session_id"); setParams(params, { replace: true }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkout = async (planId) => {
    setBusy(planId);
    try {
      const { data } = await api.post("/billing/checkout", { plan: planId, auto_renew: autoRenew, origin_url: window.location.origin });
      const url = data?.url || data?.checkout_url || data?.session_url;
      if (url) { toast.message(t("employer.plans.checkoutStarting")); window.location.href = url; }
      else toast.error(t("employer.plans.checkoutError"));
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 503 || /stripe/i.test(String(detail || ""))) {
        toast.error(t("employer.plans.stripeNotConfigured"));
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error(t("employer.plans.checkoutError"));
      }
    } finally { setBusy(null); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading text-3xl font-bold text-stone-text mb-6">{t("employer.plans.title")}</h1>
      <EmployerTabs />

      <label className="flex items-start gap-3 bg-white border border-stone-border rounded-2xl p-4 mb-6 cursor-pointer" data-testid="auto-renew-toggle">
        <input type="checkbox" checked={autoRenew} onChange={(e) => setAutoRenew(e.target.checked)} className="w-5 h-5 mt-0.5 accent-[#D95A41]" />
        <span>
          <span className="font-semibold text-stone-text flex items-center gap-2">
            {t("employer.plans.autoRenew")}
            <span className="text-xs font-bold bg-housing/15 text-housing px-2 py-0.5 rounded-full">{t("employer.plans.autoRenewSave")}</span>
          </span>
          <span className="block text-sm text-stone-muted">{t("employer.plans.autoRenewDesc")}</span>
        </span>
      </label>

      {sub?.status === "trialing" && trialDaysLeft != null && (
        <div className="bg-brand-soft border border-stone-border rounded-2xl p-4 mb-6 flex items-center gap-3" data-testid="plans-trial-banner">
          <Sparkles className="w-5 h-5 text-brand" />
          <span className="text-stone-sub">
            <b className="text-stone-text">{t("employer.overview.trialActive")}</b> · {t("employer.overview.trialDays", { days: trialDaysLeft })}
          </span>
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-stone-muted">{t("jobs.loading")}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="plans-grid">
          {plans.map((p) => {
            const isCurrent = p.id === effectivePlan;
            const features = (lang === "en" ? p.features_en : p.features_es) || [];
            return (
              <div
                key={p.id}
                data-testid={`plan-${p.id}`}
                className={`relative bg-white rounded-2xl border-2 shadow-sm p-6 flex flex-col ${isCurrent ? "border-brand" : "border-stone-border"}`}
              >
                {p.featured && (
                  <span className="absolute -top-3 left-6 flex items-center gap-1 text-xs font-bold uppercase bg-brand text-white px-3 py-1 rounded-full">
                    <Star className="w-3 h-3" /> Top
                  </span>
                )}
                <h3 className="font-heading text-xl font-bold text-stone-text">{p.name}</h3>
                <div className="mt-2 mb-4">
                  {autoRenew && p.price > 0 ? (
                    <>
                      <span className="font-heading text-4xl font-bold text-stone-text">${p.price - 1}</span>
                      <span className="text-stone-muted"> {t("employer.plans.perMonth")}</span>
                      <span className="ml-2 text-sm text-stone-muted line-through">${p.price}</span>
                    </>
                  ) : (
                    <>
                      <span className="font-heading text-4xl font-bold text-stone-text">${p.price}</span>
                      {p.price > 0 && <span className="text-stone-muted"> {t("employer.plans.perMonth")}</span>}
                    </>
                  )}
                </div>
                <ul className="space-y-2 flex-1 mb-6">
                  {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-sub">
                      <Check className="w-4 h-4 text-housing mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <div className="text-center py-3 rounded-xl bg-brand-soft text-brand font-semibold" data-testid={`plan-current-${p.id}`}>
                    {t("employer.plans.currentBtn")}
                  </div>
                ) : p.price === 0 ? (
                  <div className="text-center py-3 rounded-xl bg-stone-bg text-stone-muted font-semibold">{p.name}</div>
                ) : (
                  <button
                    data-testid={`plan-choose-${p.id}`}
                    onClick={() => checkout(p.id)}
                    disabled={busy === p.id}
                    className="py-3 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors disabled:opacity-60"
                  >
                    {busy === p.id ? "..." : t("employer.plans.upgrade", { plan: p.name })}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={successOpen} onOpenChange={setSuccessOpen}>
        <DialogContent className="sm:max-w-md" data-testid="payment-success-dialog">
          <DialogHeader>
            <div className="w-14 h-14 rounded-full bg-housing/15 flex items-center justify-center mx-auto mb-2">
              <Check className="w-7 h-7 text-housing" />
            </div>
            <DialogTitle className="text-center font-heading text-2xl">{t("employer.plans.successTitle")}</DialogTitle>
            <DialogDescription className="text-center">{t("employer.plans.successBody")}</DialogDescription>
          </DialogHeader>
          <div className="mt-2 grid grid-cols-1 gap-2">
            <button
              data-testid="receipt-download-btn"
              onClick={() => downloadReceiptTxt(receipt, user, lang)}
              className="flex items-center justify-center gap-2 py-3 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors"
            >
              <Download className="w-4 h-4" /> {t("employer.plans.downloadReceipt")}
            </button>
            <button
              data-testid="receipt-print-btn"
              onClick={() => printReceipt(receipt, user, lang)}
              className="flex items-center justify-center gap-2 py-3 rounded-xl bg-white border border-stone-border font-semibold text-stone-sub hover:border-brand hover:text-brand transition-colors"
            >
              <Printer className="w-4 h-4" /> {t("employer.plans.printReceipt")}
            </button>
            <button
              data-testid="receipt-history-btn"
              onClick={() => { setSuccessOpen(false); navigate("/employer/billing"); }}
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-brand font-semibold hover:bg-brand-soft transition-colors"
            >
              <Receipt className="w-4 h-4" /> {t("employer.plans.viewHistory")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
